"use client";
import GradientButtonIcon from "@/components/ui/custom/gradient-button-icon";
import { IconAiToolClaude, IconAiToolGemini, IconAiToolGithubCopilot, IconAiToolGrok, IconAiToolMidjourney, IconAiToolOpenai, IconAiToolReplit, IconAiToolWindsurf } from "@/lib/images";
import { ChevronDown } from "lucide-react";
import Image from "next/image";

function AIEngineeringSquadHero() {
    const handleScrollToSection = (e: React.MouseEvent) => {
        e.preventDefault();
        const element = document.getElementById('ai-devkrest-stats');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-yellow-50 pt-20 pb-32">
            {/* Floating AI Icons */}
            <div className="absolute inset-0 pointer-events-none">
                {/* Top Left - Gemini */}
                <div className="absolute top-32 left-8 md:top-40 md:left-16 animate-float-bounce opacity-100">
                    <Image src={IconAiToolGemini} alt="Gemini" className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24" />
                </div>

                {/* Top Right - Claude */}
                <div className="absolute top-28 right-6 md:top-36 md:right-12 animate-float-spin opacity-100">
                    <Image src={IconAiToolClaude} alt="Claude" className="w-14 h-14 md:w-18 md:h-18 lg:w-22 lg:h-22 rounded-2xl" />
                </div>

                {/* Bottom Left - OpenAI */}
                <div className="absolute bottom-32 left-12 md:bottom-40 md:left-20 animate-float-slow opacity-100">
                    <Image src={IconAiToolOpenai} alt="OpenAI" className="w-15 h-15 md:w-19 md:h-19 lg:w-23 lg:h-23" />
                </div>

                {/* Bottom Right - Midjourney */}
                <div className="absolute bottom-28 right-8 md:bottom-36 md:right-16 animate-float-medium opacity-100">
                    <Image src={IconAiToolMidjourney} alt="Midjourney" className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24" />
                </div>

                {/* Center Left - Grok */}
                <div className="hidden md:block absolute top-1/2 left-6 md:left-12 animate-float-fast opacity-100">
                    <Image src={IconAiToolGrok} alt="Grok" className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-2xl" />
                </div>

                {/* Center Right - GitHub Copilot */}
                <div className="hidden md:block absolute top-1/2 right-4 md:right-8 animate-float-bounce opacity-100">
                    <Image src={IconAiToolGithubCopilot} alt="GitHub Copilot" className="w-14 h-14 md:w-18 md:h-18 lg:w-22 lg:h-22 rounded-2xl" />
                </div>

                {/* Additional Icons - Replit */}
                <div className="hidden lg:block absolute bottom-20 left-1/2 -translate-x-1/2 animate-float-slow opacity-100">
                    <Image src={IconAiToolReplit} alt="Replit" className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-2xl" />
                </div>

                {/* Additional Icons - Windsurf */}
                <div className="hidden lg:block absolute bottom-1/6 right-1/4 animate-float-medium opacity-100">
                    <Image src={IconAiToolWindsurf} alt="Windsurf" className="w-13 h-13 md:w-17 md:h-17 lg:w-21 lg:h-21 rounded-2xl" />
                </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10">
                <div className="max-w-[105rem] mx-auto px-5 sm:px-10 lg:px-12 xl:px-16">
                    <div className="space-y-6">
                        {/* Title Section */}
                        <div className="relative">
                            <div className="text-center space-y-4">
                                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.1]">
                                    <span className="text-gray-900">We Don't Just Use AI.</span>
                                    <br className="hidden md:block" />
                                    <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                                        We Build With It.
                                    </span>
                                </h1>
                            </div>
                        </div>

                        {/* Subtext Section */}
                        <div className="relative flex justify-center items-center text-center">
                            <div className="max-w-4xl mx-auto px-2">
                                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                                    We've redefined our engineering process around intelligent tools. At Devkrest, AI isn't a plugin — it's a core part of how we think, design, and build better software, faster.
                                </p>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className="flex justify-center pt-4">
                            <GradientButtonIcon
                                href="#"
                                onClick={handleScrollToSection}
                                icon={<ChevronDown className="size-5" />}
                            >
                                See How We Work
                            </GradientButtonIcon>
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
}

export default AIEngineeringSquadHero; 